/**
 * Explore View Component
 *
 * Displays assets from external providers (Freesound, PolyHaven).
 * Similar structure to HomeView but focused on exploring free/open-source assets.
 */

import { useState, useMemo } from 'react';
import type { IAsset, ProviderRegistry } from '../providers';
import { useMediaAssets, useAssetSelection } from '../hooks';
import { MediaSidebar } from './MediaSidebar';
import { MediaHeader } from './MediaHeader';
import { AssetDetailPanel } from './AssetDetailPanel';
import type { Filters, AssetSource } from '../types';
import { MediaType } from '../types';
import type { AssetSelectCallback, SortOption } from '../types';
import { ProcessingIcon } from '../../../icons';
import { Dropzone } from './Dropzone';
import { MediaTabs } from './MediaTabs';
import { AssetRow } from './AssetRow';
import { extractUniqueCategoriesFromAssets } from '../utils';
import { useAssetMetadata } from '../hooks';
import { MediaGrid } from './MediaGrid';
import { InfiniteScrollContainer } from './InfiniteScrollContainer';
import { EmptyStateSection } from './EmptyStateSection';
import { DragSelectContainer } from './DragSelectContainer';

export interface ExploreViewProps {
  /** Provider registry containing external providers */
  registry: ProviderRegistry;
  /** Workspace ID for filtering ZML assets */
  workspaceId?: string;
  /** Available media types to filter by */
  enabledMediaTypes?: MediaType[];
  /** Current media type being browsed */
  mediaType?: MediaType | null;
  /** Enable multi-select mode for assets */
  enableMultiSelect?: boolean;
  /** Whether to show the multi-select toggle in the header */
  showMultiSelectToggle?: boolean;
  /** Callback to toggle multi-select mode */
  onToggleMultiSelect?: () => void;
  /** Callback when an asset is selected */
  onSelect?: AssetSelectCallback;
  /** Callback for back navigation */
  onBack?: () => void;
  /** Callback for handling file uploads */
  handleUpload?: (files: FileList) => void;
  /** Search query from parent */
  searchQuery?: string;
  /** Callback when search query changes */
  onSearchChange: (query: string) => void;
  /** Sort option from parent */
  sortBy: SortOption;
  /** Callback when sort changes */
  onSortChange?: (sort: SortOption) => void;
  /** Filters from parent */
  filters?: Filters;
  /** Callback when filters change */
  onFiltersChange?: (filters: Filters) => void;
  /** Callback to delete a single asset */
  onDelete?: (assetId: string, asset: IAsset) => Promise<boolean>;
  /** Callback to delete multiple assets */
  onDeleteMultiple?: (assetIds: string[]) => Promise<boolean>;
  /** Callback for source selection */
  onSourceSelect?: (source: AssetSource) => void;
  onOpenPremiumUploadModal?: () => void;
  /** Changing this value triggers a re-fetch of assets */
  refreshKey?: number;
  /** Custom CSS class */
  className?: string;
}

export const ExploreView = ({
  registry,
  enabledMediaTypes,
  mediaType,
  workspaceId,
  enableMultiSelect = true,
  showMultiSelectToggle = false,
  onToggleMultiSelect,
  onSelect,
  onBack,
  handleUpload,
  searchQuery = '',
  onSearchChange,
  sortBy: sortByProp,
  onSortChange,
  filters: filtersProp,
  onFiltersChange,
  onDelete,
  onDeleteMultiple,
  onSourceSelect,
  onOpenPremiumUploadModal,
  className = '',
  refreshKey,
}: ExploreViewProps) => {
  const sortBy = sortByProp;
  const filters: Filters = filtersProp ?? { animated: [], templateType: [], status: [], labels: [], categories: [] };

  const [activeSource, setActiveSource] = useState<AssetSource>('');
  const [activeProvider, setActiveProvider] = useState<string>('');
  const [filterMediaType, setFilterMediaType] = useState<MediaType | null>(mediaType || null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Get all providers
  const externalProviders = registry.getAll()

  // Load assets from each external provider
  const providerAssets = externalProviders.map(provider => {
    const providerId = provider.getId();
    const providerSource = providerId === 'zapworks' ? 'zappar-library' : providerId; // Map ZapworksProvider to 'zappar-library' source

    const { assets, loading, loadingMore, hasMore, loadMore, error } = useMediaAssets({
      registry,
      mediaType: filterMediaType || enabledMediaTypes || undefined,
      source: providerSource,
      searchQuery,
      sortBy,
      filters,
      refreshKey,
    });

    return {
      providerId,
      providerName: provider.getDisplayName(),
      state: {
        assets,
        count: assets.length,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        error
      }
    };
  });

  // Collect all assets from all providers for detail panel
  const allAssets = providerAssets.flatMap(row => row.state.assets);

  const {
    selectedAssetIds,
    selectedAssets: selectedAssetObjects,
    hiddenSelectedCount,
    toggleSelection,
    addToSelection,
    clearSelection,
  } = useAssetSelection<IAsset>({
    enableMultiSelect,
    visibleAssets: allAssets,
  });

  // Extract unique categories from all assets
  const categories = useMemo(() => {
    const providerSource = activeProvider === 'zappar-library' ? 'zapworks' : activeProvider;
    return extractUniqueCategoriesFromAssets(providerAssets.filter(asset => (asset.providerId === providerSource)).flatMap(row => row.state.assets));
  }, [activeProvider]);

  const activeProviderState = useMemo(() => {
    const providerSource = activeProvider === 'zappar-library' ? 'zapworks' : activeProvider;
    return providerAssets.find(row => row.providerId === providerSource)?.state;
  }, [activeProvider, providerAssets]);

  // Check loading state
  const isLoading = providerAssets.some(row => row.state.loading);

  // Check if any assets exist
  const hasAssets = providerAssets.some(row => row.state.assets.length > 0);


  // Handle search changes and propagate to parent
  const handleSearchChange = (query: string) => {
    onSearchChange(query);
  };

  const handleDeleteAsset = async (assetId: string, asset: IAsset) => {
    const success = await onDelete?.(assetId, asset);
    if (success) clearSelection();
  };

  const handleDeleteMultiple = async (assetIds: string[]) => {
    const success = await onDeleteMultiple?.(assetIds);
    if (success) clearSelection();
  };

  const handleAssetClick = (asset: IAsset) => {
    if (onSelect) {
      onSelect(asset);
    }
  };

  const { hasAnimated: computedShowAnimatedFilter } = useAssetMetadata(
    providerAssets.flatMap(p => p.state.assets)
  );

  const handleSortChange = (sort: SortOption) => {
    onSortChange?.(sort);
  };

  const handleFiltersChange = (newFilters: Filters) => {
    onFiltersChange?.(newFilters);
  };

  const handleSourceChange = (source: AssetSource) => {
    resetView();
    if (source !== 'explore') {
      setActiveSource(source);
      onSourceSelect?.(source);
    }
  };

  const resetView = () => {
    setFilterMediaType(null);
    setActiveSource('');
    setActiveProvider('');
    setSelectedCategory(null);
    onFiltersChange?.({
      ...filters,
      categories: [],
    });
  }

  const handleViewAll = (providerId: string) => {
    const provider = registry.get(providerId);
    if (provider) {
      const source: AssetSource = providerId === 'zapworks' ? 'zappar-library' : providerId;
      setActiveProvider(source);
    }
  };

  const renderBreadcrumb = () => {
    if (activeProvider) {
      if (selectedCategory) {
        return (
          <p>
            Explore {filterMediaType || 'all'} assets / {activeProvider} / <b>{selectedCategory}</b>
          </p>
        )
      }
      return (
        <p>
          Explore {filterMediaType || 'all'} assets / <b>{activeProvider}</b>
        </p>
      );
    }
  };


  const handleCategorySelect = (category: string | null) => {
    setSelectedCategory(category);
    onFiltersChange?.({
      ...filters,
      categories: category ? [category] : [],
    });
  }

  return (
    <div className={`library-view library-view--with-sidebar ${className}`}>
      <div className="library-view__sidebar">
        <MediaSidebar
          registry={registry}
          mediaType={mediaType}
          showWorkspaceAssets={!!workspaceId}
          activeSource={activeSource || 'explore'}
          onSourceChange={handleSourceChange}
          onBack={onBack}
          isHomeView={false}
          showCategories={!!activeProvider}
          categories={categories}
          selectedCategory={selectedCategory}
          onCategorySelect={handleCategorySelect}
        />
      </div>

      <div className="library-view__main">
        <div className="library-view__header">
          <MediaHeader
            searchQuery={searchQuery}
            sortBy={sortBy}
            filters={filters}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            onFiltersChange={handleFiltersChange}
            onUpload={handleUpload}
            acceptedFileTypes={mediaType ? [mediaType] : enabledMediaTypes}
            showAnimatedFilter={computedShowAnimatedFilter}
            showMultiSelectToggle={showMultiSelectToggle}
            enableMultiSelect={enableMultiSelect}
            onToggleMultiSelect={onToggleMultiSelect}
            onOpenPremiumUploadModal={onOpenPremiumUploadModal}
          />
        </div>


        {activeProvider ? (
          <div className="library-view__title">
            {renderBreadcrumb()}
          </div>
        ) : (
          !mediaType && enabledMediaTypes && enabledMediaTypes?.length > 1 && (
            <div className="library-view__tabs">
              <MediaTabs
                mediaTypes={enabledMediaTypes || Object.values(MediaType).filter(type => type !== MediaType.Unknown) as MediaType[]}
                activeType={filterMediaType}
                onTabChange={setFilterMediaType}
              />
            </div>
          )
        )}

        <DragSelectContainer enabled={enableMultiSelect} onSelect={toggleSelection} onSelectMany={addToSelection}>
          {({ dragHighlightedIds, isDragging }) => (
            <Dropzone onUpload={handleUpload}>
              <div className="library-view__content">
                {/* Empty State */}
                {!hasAssets && !isLoading ? (
                  <EmptyStateSection
                    sectionTitle={activeProvider ? activeProvider : 'Explore'}
                    tab={activeProvider ? `${selectedCategory}` : filterMediaType ? `${filterMediaType}` : ''}
                    searchQuery={searchQuery}
                  />
                ) :
                  isLoading ? (
                    <div className="library-view__loading">
                      <ProcessingIcon />
                    </div>
                  ) : (
                    <>
                      {/* If activeSource exists, show MediaGrid */}
                      {activeProvider ? (
                        <InfiniteScrollContainer
                          onLoadMore={activeProviderState?.loadMore ?? (() => {})}
                          hasMore={activeProviderState?.hasMore ?? false}
                          loadingMore={activeProviderState?.loadingMore ?? false}
                          loader={<div className="library-view__loading"><ProcessingIcon /></div>}
                        >
                          <MediaGrid
                            assets={activeProviderState?.assets || []}
                            onAssetClick={handleAssetClick}
                            onAssetSelect={toggleSelection}
                            selectedAssets={selectedAssetIds}
                            dragHighlightedIds={dragHighlightedIds}
                            isDragActive={isDragging}
                          />
                        </InfiniteScrollContainer>
                      ) : (
                        providerAssets.map((provider) => (
                          <AssetRow
                            key={provider.providerId}
                            title={provider.providerName}
                            count={provider.state.count}
                            state={provider.state}
                            enableMultiSelect={enableMultiSelect}
                            onAssetClick={handleAssetClick}
                            onAssetSelect={toggleSelection}
                            onViewAll={() => handleViewAll(provider.providerId)}
                            selectedAssets={selectedAssetIds}
                            dragHighlightedIds={dragHighlightedIds}
                          />
                        ))
                      )}

                    </>
                  )}
              </div>
            </Dropzone>
          )}
        </DragSelectContainer>
      </div>

      <div className="library-view__detail">
        <AssetDetailPanel
          selectedAssets={selectedAssetObjects}
          selectedAssetIds={selectedAssetIds}
          totalSelectedCount={selectedAssetIds.length}
          hiddenSelectedCount={hiddenSelectedCount}
          activeSource={activeSource || 'explore'}
          onDelete={handleDeleteAsset}
          onDeleteMultiple={handleDeleteMultiple}
          onDeselectAll={clearSelection}
          disableEditName={true}
          isExploreView={true}
          key={refreshKey}
        />
      </div>
    </div>
  );
};
